// Blog post metadata interface
export interface PostMeta {
  title: string;
  excerpt: string;
  slug: string;
  date: string;
  icon?: any; // For Lucide React icons
}

// Blog post structure
export interface BlogPost {
  meta: PostMeta;
  content: string;
}

// Parse frontmatter from markdown content
function parseFrontmatter(content: string): { frontmatter: Record<string, string>; content: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  
  if (!match) {
    throw new Error('Invalid markdown format: missing frontmatter');
  }
  
  const frontmatterText = match[1];
  const markdownContent = match[2];
  
  // Parse YAML-like frontmatter
  const frontmatter: Record<string, string> = {};
  const lines = frontmatterText.split('\n');
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith('#')) {
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex > 0) {
        const key = trimmed.substring(0, colonIndex).trim();
        const value = trimmed.substring(colonIndex + 1).trim().replace(/^["']|["']$/g, '');
        frontmatter[key] = value;
      }
    }
  }
  
  return { frontmatter, content: markdownContent };
}

// Get blog post metadata with pagination
export async function getPostMetas(page = 1, limit = 12): Promise<{
  posts: PostMeta[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}> {
  try {
    // Import all markdown files from the content/blog directory
    const blogModules = import.meta.glob('/src/content/blog/*.md', { as: 'raw' });
    const postMetas: PostMeta[] = [];
    
    for (const [path, module] of Object.entries(blogModules)) {
      try {
        const content = await module() as string;
        const { frontmatter } = parseFrontmatter(content);
        
        // Extract slug from filename if not provided in frontmatter
        const slug = frontmatter.slug || path.split('/').pop()?.replace('.md', '') || '';
        
        postMetas.push({
          title: frontmatter.title || 'Untitled',
          excerpt: frontmatter.excerpt || '',
          slug,
          date: frontmatter.date || '',
        });
      } catch (error) {
        console.warn(`Failed to parse blog post at ${path}:`, error);
      }
    }
    
    // Sort by date (newest first)
    const sortedPosts = postMetas.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
    
    const total = sortedPosts.length;
    const totalPages = Math.ceil(total / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const posts = sortedPosts.slice(startIndex, endIndex);
    
    return {
      posts,
      total,
      hasMore: endIndex < total,
      currentPage: page,
      totalPages,
    };
  } catch (error) {
    console.error('Failed to load blog posts:', error);
    return {
      posts: [],
      total: 0,
      hasMore: false,
      currentPage: 1,
      totalPages: 0,
    };
  }
}

// Get a specific blog post by slug
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  try {
    // Try to import the specific markdown file
    const blogModules = import.meta.glob('/src/content/blog/*.md', { as: 'raw' });
    
    for (const [path, module] of Object.entries(blogModules)) {
      const filename = path.split('/').pop()?.replace('.md', '');
      if (filename === slug) {
        const content = await module() as string;
        const { frontmatter, content: markdownContent } = parseFrontmatter(content);
        
        
        return {
          meta: {
            title: frontmatter.title || 'Untitled',
            excerpt: frontmatter.excerpt || '',
            slug: frontmatter.slug || filename || '',
            date: frontmatter.date || '',
          },
          content: markdownContent,
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error(`Failed to load blog post with slug ${slug}:`, error);
    return null;
  }
}
